import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Radio,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  ButtonGroup,
  LinearProgress,
  Stack,
  Avatar
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Timer as TimerIcon,
  Quiz as QuizIcon,
  Send as SendIcon,
  WhatsApp as WhatsAppIcon,
  School as SchoolIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useLanguage, Language } from '../../Commun/contexts/LanguageContext';

interface PublicOption {
  QOP_KEY: string;
  QOP_LABEL: string;
  QOP_ORDER?: number;
}

interface PublicQuestion {
  QST_IDF: number;
  QST_TEXT: string;
  QST_TYPE: string;           // 'single' | 'multi' | 'boolean' | 'text'
  OPTIONS: PublicOption[];
  QST_ORDER: number;
  QST_POINTS: number;
}

interface PublicLevel {
  LVL_CODE: string;
  LVL_TITLE: string;
  LVL_MIN_SCORE: number;
  LVL_MAX_SCORE: number;
  LVL_MESSAGE: string;
}

interface AgencyData {
  DOS_LIB?: string;
  DOS_EMAIL?: string;
  DOS_TELPH?: string;
  DOS_ADR1?: string;
  DOS_LOGO?: string;
}

interface QuizData {
  QIZ_IDF: number;
  QIZ_CODE: string;
  QIZ_TITLE: string;
  QIZ_DESCRIPTION: string;
  QIZ_DURATION_MINUTES: number;
  QIZ_EXPIRY_DAYS: number;
  QIZ_WHATSAPP: string;
  QIZ_REGISTER_LINK: string;
  QIZ_DISPLAY: string;
  QIZ_AUTO_NEXT?: boolean;
  AGENCY?: AgencyData;
  QUESTIONS: PublicQuestion[];
  LEVELS: PublicLevel[];
}

const PUBLIC_BASE = '/api/public/7DD983F4E7F84606912921C8FE1602AD';

const AGE_BANDS = ['under_12', '12_15', '16_25', '26_35', '36_50', 'over_50'];

export const AcademyQuizPage: React.FC = () => {
  const { t, language, setLanguage, dir } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [quiz, setQuiz] = useState<QuizData | null>(null);
  const [started, setStarted] = useState<boolean>(false);
  const [showDetails, setShowDetails] = useState<boolean>(false);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [ageBand, setAgeBand] = useState('');

  const [answers, setAnswers] = useState<Record<number, string | string[]>>({});
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const [result, setResult] = useState<{ score: number; total: number; levelCode: string; refCode: string } | null>(null);

  const autoNextTimerRef = useRef<NodeJS.Timeout | null>(null);

  const displayMode: 'list' | 'assistant' = quiz?.QIZ_DISPLAY === 'assistant' ? 'assistant' : 'list';

  useEffect(() => {
    document.title = `${quiz?.QIZ_TITLE || 'Quiz'} - Blobtic Academy`;
  }, [quiz]);

  useEffect(() => {
    const fetchQuiz = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${PUBLIC_BASE}/get_quiz_page?token=${encodeURIComponent(token)}`);
        const resultJson = await response.json();
        if (response.ok && resultJson && resultJson.success && resultJson.data) {
          const item = Array.isArray(resultJson.data) ? resultJson.data[0] : resultJson.data;
          setQuiz(item);
        } else {
          setError(resultJson?.message || resultJson?.error || t('academy.quiz_public.error_invalid_token'));
        }
      } catch {
        setError(t('academy.quiz_public.error_load_failed'));
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchQuiz();
    else {
      setLoading(false);
      setError(t('academy.quiz_public.error_no_token'));
    }
  }, [token, t]);

  // Countdown timer
  useEffect(() => {
    if (!started || !quiz || result) return;
    if (secondsLeft <= 0) return;
    const interval = setInterval(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearInterval(interval);
  }, [started, quiz, secondsLeft, result]);

  // Cleanup auto next timer on unmount
  useEffect(() => {
    return () => {
      if (autoNextTimerRef.current) {
        clearTimeout(autoNextTimerRef.current);
      }
    };
  }, []);

  const startQuiz = () => {
    if (!quiz) return;
    setSecondsLeft(quiz.QIZ_DURATION_MINUTES * 60);
    setCurrentIndex(0);
    setStarted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isValidEmailFormat = (em: string): boolean => {
    if (!em.trim()) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em.trim());
  };

  const isPhoneValidFormat = (ph: string): boolean => {
    const clean = ph.replace(/[^\d+]/g, '');
    return clean.length >= 8 && clean.length <= 20;
  };

  const isFormValid = fullName.trim().length >= 3 && isPhoneValidFormat(phone) && isValidEmailFormat(email);

  const isQuestionAnswered = (q: PublicQuestion): boolean => {
    const a = answers[q.QST_IDF];
    if (a === undefined || a === null) return false;
    if (q.QST_TYPE === 'multi') return Array.isArray(a) && a.length > 0;
    return typeof a === 'string' && a.trim() !== '';
  };

  const allAnswered = quiz && quiz.QUESTIONS.length > 0 && quiz.QUESTIONS.every(isQuestionAnswered);

  const submit = async () => {
    if (!quiz) return;

    if (fullName.trim().length < 3) {
      setError(t('academy.quiz_public.error_name_invalid') || 'Veuillez saisir un nom complet valide (minimum 3 caractères).');
      return;
    }

    if (!isPhoneValidFormat(phone)) {
      setError(t('academy.quiz_public.error_phone_invalid') || `Le numéro de téléphone '${phone}' n'est pas valide (minimum 8 chiffres requis).`);
      return;
    }

    if (!isValidEmailFormat(email)) {
      setError(t('academy.quiz_public.error_email_invalid') || `L'adresse email '${email}' n'est pas valide (exemple: nom@domaine.com).`);
      return;
    }

    if (!allAnswered) {
      setError(t('academy.quiz_public.error_all_questions'));
      return;
    }

    setSubmitting(true);
    setError(null);

    const answerList = quiz.QUESTIONS.map(q => ({ qst_id: q.QST_IDF, answer: answers[q.QST_IDF] }));
    const durationSeconds = quiz.QIZ_DURATION_MINUTES * 60 - secondsLeft;

    try {
      const response = await fetch(`${PUBLIC_BASE}/submit_quiz_attempt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          ATT_FULL_NAME: fullName,
          ATT_PHONE: phone,
          ATT_EMAIL: email,
          ATT_AGE_BAND: ageBand,
          ATT_ANSWERS: JSON.stringify(answerList),
          ATT_DURATION_SECONDS: durationSeconds,
          language
        })
      });
      const resultJson = await response.json();

      if (resultJson && resultJson.success && resultJson.data) {
        const item = Array.isArray(resultJson.data) ? resultJson.data[0] : resultJson.data;
        setResult({
          score: item.SCORE ?? 0,
          total: item.TOTAL ?? 0,
          levelCode: item.LEVEL_CODE ?? '',
          refCode: item.REFERENCE_CODE ?? '',
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError(resultJson?.message || resultJson?.error || t('academy.quiz_public.error_submit_failed'));
      }
    } catch {
      setError(t('academy.quiz_public.error_submit_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  const resultLevel = useMemo(() => {
    if (!quiz || !result) return null;
    return quiz.LEVELS.find(l => l.LVL_CODE === result.levelCode) || null;
  }, [quiz, result]);

  const pct = result && result.total > 0 ? Math.round((result.score / result.total) * 100) : 0;
  const isTimeUp = started && secondsLeft <= 0;
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  // Single option selection handler with debounced Auto-Next support
  const handleSelectOption = (qId: number, optKey: string, qType: string) => {
    setAnswers(prev => ({ ...prev, [qId]: optKey }));

    const isAutoNext = quiz?.QIZ_AUTO_NEXT ?? true;
    if (isAutoNext && (qType === 'single' || qType === 'boolean')) {
      if (displayMode === 'assistant') {
        if (autoNextTimerRef.current) {
          clearTimeout(autoNextTimerRef.current);
        }

        autoNextTimerRef.current = setTimeout(() => {
          setCurrentIndex(prevIndex => {
            const totalQuestions = quiz?.QUESTIONS.length ?? 1;
            if (prevIndex < totalQuestions - 1) {
              return prevIndex + 1;
            } else {
              setShowDetails(true);
              window.scrollTo({ top: 0, behavior: 'smooth' });
              return prevIndex;
            }
          });
        }, 350);
      }
    }
  };

  const renderAgencyHeader = () => {
    const agency = quiz?.AGENCY;
    if (!agency || (!agency.DOS_LIB && !agency.DOS_TELPH && !agency.DOS_EMAIL && !agency.DOS_ADR1)) return null;

    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: 3,
          mb: 3,
          bgcolor: 'background.paper',
          background: 'linear-gradient(135deg, rgba(14, 116, 144, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {agency.DOS_LOGO ? (
              <Box
                component="img"
                src={agency.DOS_LOGO}
                alt={agency.DOS_LIB || 'Agency Logo'}
                sx={{ maxHeight: 52, maxWidth: 160, objectFit: 'contain', borderRadius: 1.5 }}
              />
            ) : (
              <Avatar sx={{ bgcolor: 'primary.main', color: '#fff', width: 44, height: 44 }}>
                <BusinessIcon />
              </Avatar>
            )}
            <Box>
              {agency.DOS_LIB && (
                <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ lineHeight: 1.2 }}>
                  {agency.DOS_LIB}
                </Typography>
              )}
              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {agency.DOS_TELPH && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                    <PhoneIcon fontSize="inherit" color="primary" /> <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'embed' }}>{agency.DOS_TELPH}</span>
                  </Typography>
                )}
                {agency.DOS_EMAIL && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                    <EmailIcon fontSize="inherit" color="primary" /> {agency.DOS_EMAIL}
                  </Typography>
                )}
                {agency.DOS_ADR1 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                    <LocationIcon fontSize="inherit" color="primary" /> {agency.DOS_ADR1}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderAgencyFooter = () => {
    const agency = quiz?.AGENCY;

    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        {agency && (agency.DOS_LIB || agency.DOS_TELPH || agency.DOS_EMAIL || agency.DOS_ADR1) && (
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc',
              textAlign: 'center',
              mb: 2
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
              {t('academy.quiz_public.offered_by', agency.DOS_LIB || 'Blobtic Academy')}
            </Typography>
            <Stack direction="row" spacing={2.5} justifyContent="center" flexWrap="wrap">
              {agency.DOS_TELPH && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon fontSize="small" color="primary" /> <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'embed' }}>{agency.DOS_TELPH}</span>
                </Typography>
              )}
              {agency.DOS_EMAIL && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon fontSize="small" color="primary" /> {agency.DOS_EMAIL}
                </Typography>
              )}
              {agency.DOS_ADR1 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationIcon fontSize="small" color="primary" /> {agency.DOS_ADR1}
                </Typography>
              )}
            </Stack>
          </Paper>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600, opacity: 0.85 }}>
          {t('common.copyright')}
        </Typography>
      </Box>
    );
  };

  const renderContactForm = () => (
    <Grid container spacing={2.5}>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          label={t('academy.quiz_public.full_name')}
          fullWidth
          size="small"
          value={fullName}
          onChange={e => setFullName(e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          label={t('academy.quiz_public.phone')}
          fullWidth
          size="small"
          value={phone}
          onChange={e => setPhone(e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label={t('academy.quiz_public.email')}
          type="email"
          fullWidth
          size="small"
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label={t('academy.quiz_public.age_band')}
          select
          fullWidth
          size="small"
          value={ageBand}
          onChange={e => setAgeBand(e.target.value)}
        >
          {AGE_BANDS.map(band => (
            <MenuItem key={band} value={band}>{t(`academy.quiz_public.age_band_${band}`)}</MenuItem>
          ))}
        </TextField>
      </Grid>
    </Grid>
  );

  // ─── LOADING ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <CircularProgress size={48} color="primary" />
      </Box>
    );
  }

  // ─── ERROR ─────────────────────────────────────────────────────────
  if (error && !quiz) {
    return (
      <Box dir={dir} sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 5, textAlign: 'center', borderRadius: 4 }}>
            <Box sx={{ bgcolor: 'error.light', color: 'error.main', width: 72, height: 72, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <Typography variant="h5" fontWeight={900}>404</Typography>
            </Box>
            <Typography variant="h5" fontWeight={800} color="text.primary" sx={{ mb: 1 }}>
              {t('academy.quiz_public.error_invalid_token')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // ─── RESULT ────────────────────────────────────────────────────────
  if (result && quiz) {
    return (
      <Box dir={dir} sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 8 }}>
        <Container maxWidth="sm">
          {renderAgencyHeader()}
          <Paper elevation={3} sx={{ p: 5, textAlign: 'center', borderRadius: 4 }}>
            <SuccessIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ mb: 1 }}>
              {t('academy.quiz_public.result_title')}
            </Typography>

            <Chip
              label={t('academy.quiz_public.result_ref', result.refCode)}
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.9rem', py: 1.5, px: 2, borderRadius: 3, fontWeight: 700, mb: 3 }}
            />

            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t('academy.quiz_public.result_score', result.score, result.total)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={pct}
                color={pct >= 60 ? 'success' : 'warning'}
                sx={{ borderRadius: 3, height: 10, mb: 1 }}
              />
              <Typography variant="body2" fontWeight={700} color="text.secondary">
                {pct}%
              </Typography>
            </Box>

            {resultLevel && (
              <Alert severity="info" sx={{ borderRadius: 3, mb: 3, textAlign: dir === 'rtl' ? 'right' : 'left' }}>
                <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5 }}>
                  {t('academy.quiz_public.result_level', resultLevel.LVL_TITLE)}
                </Typography>
                {resultLevel.LVL_MESSAGE && (
                  <Typography variant="body2">{resultLevel.LVL_MESSAGE}</Typography>
                )}
              </Alert>
            )}

            <Grid container spacing={1.5}>
              {quiz.QIZ_WHATSAPP && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    component="a"
                    href={`https://wa.me/${quiz.QIZ_WHATSAPP.replace(/\D/g, '')}?text=${encodeURIComponent(t('academy.quiz_public.whatsapp_result_text', result.refCode))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<WhatsAppIcon />}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                  >
                    {t('academy.quiz_public.whatsapp_btn')}
                  </Button>
                </Grid>
              )}
              {quiz.QIZ_REGISTER_LINK && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    component="a"
                    href={quiz.QIZ_REGISTER_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<SchoolIcon />}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                  >
                    {t('academy.quiz_public.register_btn')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>
          {renderAgencyFooter()}
        </Container>
      </Box>
    );
  }

  // ─── INTRO ─────────────────────────────────────────────────────────
  if (!started) {
    return (
      <Box dir={dir} sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 3, md: 6 } }}>
        <Container maxWidth="md">
          {renderAgencyHeader()}

          <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ bgcolor: 'primary.main', color: '#fff', p: 1.5, borderRadius: 3, display: 'flex' }}>
                  <QuizIcon fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={800} color="primary.main">
                    {quiz!.QIZ_TITLE}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('academy.quiz_public.public_portal')}
                  </Typography>
                </Box>
              </Box>
              <ButtonGroup size="small" variant="outlined">
                {(['fr', 'en', 'ar'] as Language[]).map(lang => (
                  <Button
                    key={lang}
                    variant={language === lang ? 'contained' : 'outlined'}
                    onClick={() => setLanguage(lang)}
                    sx={{ px: 1.5, py: 0.5, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase' }}
                  >
                    {lang}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          </Paper>

          <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3, bgcolor: 'background.paper' }}>
            {quiz!.QIZ_DESCRIPTION && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  mb: 3,
                  whiteSpace: 'pre-line',
                  lineHeight: 1.7,
                  fontSize: '1.02rem'
                }}
              >
                {quiz!.QIZ_DESCRIPTION}
              </Typography>
            )}

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Chip
                  icon={<TimerIcon />}
                  label={t('academy.quiz_public.duration_label', quiz!.QIZ_DURATION_MINUTES)}
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '0.95rem', fontWeight: 700, py: 1.5, px: 2, borderRadius: 3, width: '100%', justifyContent: 'flex-start' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Chip
                  label={t('academy.quiz_public.questions_count', quiz!.QUESTIONS.length)}
                  color="info"
                  variant="outlined"
                  sx={{ fontSize: '0.95rem', fontWeight: 700, py: 1.5, px: 2, borderRadius: 3, width: '100%', justifyContent: 'flex-start' }}
                />
              </Grid>
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 3 }}>{error}</Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={startQuiz}
                endIcon={<TimerIcon />}
                sx={{ textTransform: 'none', borderRadius: 2.5, px: 4, py: 1.2, fontWeight: 700, fontSize: '1rem' }}
              >
                {t('academy.quiz_public.start_btn')}
              </Button>
            </Box>
          </Paper>
          {renderAgencyFooter()}
        </Container>
      </Box>
    );
  }

  // ─── DETAILS (enter info before seeing the result) ─────────────────
  if (showDetails) {
    return (
      <Box dir={dir} sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 3, md: 6 } }}>
        <Container maxWidth="md">
          {renderAgencyHeader()}
          <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <QuizIcon color="primary" />
                <Typography variant="h6" fontWeight={800}>
                  {quiz!.QIZ_TITLE}
                </Typography>
              </Box>
              <Chip
                icon={<TimerIcon />}
                label={`${minutes}:${String(seconds).padStart(2, '0')}`}
                color={secondsLeft < 60 ? 'error' : 'primary'}
                sx={{ fontSize: '1rem', fontWeight: 800, py: 1, px: 2, borderRadius: 3 }}
              />
            </Box>
          </Paper>

          <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <SuccessIcon sx={{ color: 'success.main' }} />
              <Typography variant="h6" fontWeight={800}>
                {t('academy.quiz_public.contact_header')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('academy.quiz_public.details_subtitle')}
            </Typography>

            {renderContactForm()}

            {error && (
              <Alert severity="error" sx={{ mt: 3, borderRadius: 3 }}>{error}</Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
              <Button
                variant="outlined"
                disabled={isTimeUp || submitting}
                onClick={() => setShowDetails(false)}
                startIcon={<PrevIcon />}
                sx={{ textTransform: 'none', borderRadius: 2, px: 4, fontWeight: 700 }}
              >
                {t('academy.quiz_public.prev_btn')}
              </Button>
              <Button
                variant="contained"
                color="success"
                size="large"
                disabled={!isFormValid || submitting}
                onClick={submit}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                sx={{ textTransform: 'none', borderRadius: 2, px: 5, fontWeight: 700 }}
              >
                {submitting ? t('academy.quiz_public.submitting_btn') : t('academy.quiz_public.submit_btn')}
              </Button>
            </Box>
          </Paper>
          {renderAgencyFooter()}
        </Container>
      </Box>
    );
  }

  // ─── QUIZ IN PROGRESS ──────────────────────────────────────────────
  const totalQ = quiz!.QUESTIONS.length;
  const isLastQuestion = currentIndex >= totalQ - 1;

  const renderQuestion = (q: PublicQuestion, idx: number) => {
    const selected = answers[q.QST_IDF];
    const opts = q.OPTIONS || [];
    const showBorder = displayMode === 'list' && idx < totalQ - 1;

    const renderAnswers = () => {
      if (q.QST_TYPE === 'text') {
        return (
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            placeholder={t('academy.quiz_public.text_answer_placeholder')}
            value={typeof selected === 'string' ? selected : ''}
            onChange={e => setAnswers(prev => ({ ...prev, [q.QST_IDF]: e.target.value }))}
          />
        );
      }

      if (q.QST_TYPE === 'multi') {
        return (
          <Grid container spacing={1.5}>
            {opts.map(opt => {
              const checked = Array.isArray(selected) && selected.includes(opt.QOP_KEY);
              return (
                <Grid item xs={12} sm={6} key={opt.QOP_KEY}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.8,
                      borderRadius: 2.5,
                      cursor: 'pointer',
                      borderColor: checked ? 'primary.main' : 'divider',
                      bgcolor: checked ? 'rgba(14, 116, 144, 0.08)' : 'background.paper',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': { borderColor: 'primary.main', bgcolor: checked ? 'rgba(14, 116, 144, 0.12)' : 'rgba(0,0,0,0.01)' }
                    }}
                    onClick={() => {
                      const cur = Array.isArray(selected) ? selected : [];
                      const next = cur.includes(opt.QOP_KEY)
                        ? cur.filter(k => k !== opt.QOP_KEY)
                        : [...cur, opt.QOP_KEY];
                      setAnswers(prev => ({ ...prev, [q.QST_IDF]: next }));
                    }}
                  >
                    <FormControlLabel
                      control={<Checkbox size="small" checked={checked} />}
                      label={
                        <Typography variant="body2" fontWeight={checked ? 700 : 500}>
                          {q.QST_TYPE === 'boolean' ? '' : <><strong>{opt.QOP_KEY}.</strong> </>}{opt.QOP_LABEL}
                        </Typography>
                      }
                      sx={{ m: 0, width: '100%', pointerEvents: 'none' }}
                    />
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        );
      }

      // single / boolean
      return (
        <Grid container spacing={1.5}>
          {opts.map(opt => {
            const isSel = selected === opt.QOP_KEY;
            return (
              <Grid item xs={12} sm={6} key={opt.QOP_KEY}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.8,
                    borderRadius: 2.5,
                    cursor: 'pointer',
                    borderColor: isSel ? 'primary.main' : 'divider',
                    bgcolor: isSel ? 'rgba(14, 116, 144, 0.08)' : 'background.paper',
                    boxShadow: isSel ? '0 2px 8px rgba(14, 116, 144, 0.15)' : 'none',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': { borderColor: 'primary.main', bgcolor: isSel ? 'rgba(14, 116, 144, 0.12)' : 'rgba(0,0,0,0.01)' }
                  }}
                  onClick={() => handleSelectOption(q.QST_IDF, opt.QOP_KEY, q.QST_TYPE)}
                >
                  <FormControlLabel
                    value={opt.QOP_KEY}
                    control={<Radio size="small" checked={isSel} />}
                    label={
                      <Typography variant="body2" fontWeight={isSel ? 700 : 500}>
                        {q.QST_TYPE === 'boolean' ? '' : <><strong>{opt.QOP_KEY}.</strong> </>}{opt.QOP_LABEL}
                      </Typography>
                    }
                    sx={{ m: 0, width: '100%', pointerEvents: 'none' }}
                  />
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      );
    };

    return (
      <Box key={q.QST_IDF} sx={{ mb: 4, pb: 3, borderBottom: showBorder ? 1 : 0, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
          <Chip label={idx + 1} color="primary" size="small" sx={{ fontWeight: 800, mt: 0.3, flexShrink: 0 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, lineHeight: 1.5 }}>
            {q.QST_TEXT}
          </Typography>
        </Box>
        {renderAnswers()}
      </Box>
    );
  };

  return (
    <Box dir={dir} sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 3, md: 6 } }}>
      <Container maxWidth="md">
        {renderAgencyHeader()}

        <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3, bgcolor: 'background.paper', position: 'sticky', top: 12, zIndex: 10, boxShadow: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <QuizIcon color="primary" />
              <Typography variant="h6" fontWeight={800}>
                {quiz!.QIZ_TITLE}
              </Typography>
            </Box>
            <Chip
              icon={<TimerIcon />}
              label={`${minutes}:${String(seconds).padStart(2, '0')}`}
              color={secondsLeft < 60 ? 'error' : 'primary'}
              sx={{ fontSize: '1rem', fontWeight: 800, py: 1, px: 2, borderRadius: 3 }}
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={(secondsLeft / (quiz!.QIZ_DURATION_MINUTES * 60)) * 100}
            color={secondsLeft < 60 ? 'error' : 'primary'}
            sx={{ mt: 1.5, borderRadius: 2, height: 6 }}
          />
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>{error}</Alert>
        )}

        <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3, bgcolor: 'background.paper', mb: 3 }}>
          {displayMode === 'assistant' && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {t('academy.quiz_public.question_of', currentIndex + 1, totalQ)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={totalQ ? ((currentIndex + 1) / totalQ) * 100 : 0}
                sx={{ mt: 0.5, borderRadius: 2, height: 5 }}
              />
            </Box>
          )}
          {displayMode === 'list'
            ? quiz!.QUESTIONS.map((q, idx) => renderQuestion(q, idx))
            : renderQuestion(quiz!.QUESTIONS[currentIndex]!, currentIndex)}
        </Paper>

        {displayMode === 'assistant' ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Button
              variant="outlined"
              disabled={currentIndex === 0 || isTimeUp || submitting}
              onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
              startIcon={<PrevIcon />}
              sx={{ textTransform: 'none', borderRadius: 2, px: 4, fontWeight: 700 }}
            >
              {t('academy.quiz_public.prev_btn')}
            </Button>
            <Button
              variant="contained"
              color={isLastQuestion ? 'success' : 'primary'}
              disabled={!isQuestionAnswered(quiz!.QUESTIONS[currentIndex]!) || isTimeUp || submitting}
              onClick={() => (isLastQuestion ? setShowDetails(true) : setCurrentIndex(i => Math.min(totalQ - 1, i + 1)))}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : (isLastQuestion ? <SendIcon /> : <NextIcon />)}
              sx={{ textTransform: 'none', borderRadius: 2, px: 5, fontWeight: 700 }}
            >
              {submitting
                ? t('academy.quiz_public.submitting_btn')
                : (isLastQuestion ? t('academy.quiz_public.continue_btn') : t('academy.quiz_public.next_btn'))}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={!allAnswered || isTimeUp || submitting}
              onClick={() => setShowDetails(true)}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              sx={{ textTransform: 'none', borderRadius: 2, px: 5, fontWeight: 700 }}
            >
              {submitting ? t('academy.quiz_public.submitting_btn') : t('academy.quiz_public.continue_btn')}
            </Button>
          </Box>
        )}
        {renderAgencyFooter()}
      </Container>
    </Box>
  );
};

export default AcademyQuizPage;
